import type { PersonalFlowCard } from '../api/schemas';

export const PERSONAL_FLOW_PAGE_LIMIT = 40;
export const PERSONAL_FLOW_SENTINEL_LIMIT = 1;

export type PersonalFlowSectionKey = 'certification-registration' | 'training-program' | 'general';

export type PersonalFlowSectionBudgets = Record<PersonalFlowSectionKey, number>;

export interface PersonalFlowSectionCards {
  key: PersonalFlowSectionKey;
  cards: PersonalFlowCard[];
}

export interface PersonalFlowSectionExpansionCandidate extends PersonalFlowSectionCards {
  isReady: boolean;
}

const PERSONAL_FLOW_SOURCE_ORDER = [
  'welcome_program',
  'onboarding_program',
  'certification_registration',
  'online_course_registration',
  'main_program',
  'news',
] as const;

function canonicalCardSource(card: PersonalFlowCard) {
  return card.source_type === 'training_journal'
    ? card.training_journal_context?.personal_flow_source_type
    : card.source_type;
}

export function createPersonalFlowSectionBudgets(input: {
  hasRegistration: boolean;
  hasTrainingProgram: boolean;
}): PersonalFlowSectionBudgets {
  if (input.hasRegistration) {
    return {
      'certification-registration': PERSONAL_FLOW_PAGE_LIMIT,
      'training-program': PERSONAL_FLOW_SENTINEL_LIMIT,
      general: PERSONAL_FLOW_SENTINEL_LIMIT,
    };
  }

  if (input.hasTrainingProgram) {
    return {
      'certification-registration': PERSONAL_FLOW_SENTINEL_LIMIT,
      'training-program': PERSONAL_FLOW_PAGE_LIMIT,
      general: PERSONAL_FLOW_SENTINEL_LIMIT,
    };
  }

  return {
    'certification-registration': PERSONAL_FLOW_SENTINEL_LIMIT,
    'training-program': PERSONAL_FLOW_SENTINEL_LIMIT,
    general: PERSONAL_FLOW_PAGE_LIMIT,
  };
}

export function selectPersonalFlowSectionCards(
  sections: PersonalFlowSectionCards[],
  limit = PERSONAL_FLOW_PAGE_LIMIT
): PersonalFlowSectionCards[] {
  const sourcePriority = new Map(
    PERSONAL_FLOW_SOURCE_ORDER.map((source, index) => [source, index])
  );
  const priorityOf = (source: ReturnType<typeof canonicalCardSource>) =>
    source === undefined
      ? Number.MAX_SAFE_INTEGER
      : (sourcePriority.get(source) ?? Number.MAX_SAFE_INTEGER);
  const candidates = sections.flatMap(({ key, cards }, sectionIndex) =>
    cards.map((card, cardIndex) => ({
      card,
      key,
      order: sectionIndex * PERSONAL_FLOW_PAGE_LIMIT + cardIndex,
      source: canonicalCardSource(card),
    }))
  );
  const candidatesBySlide = new Map<string, (typeof candidates)[number]>();
  for (const candidate of candidates) {
    const existing = candidatesBySlide.get(candidate.card.slide_id);
    const candidatePriority = priorityOf(candidate.source);
    const existingPriority =
      existing === undefined ? Number.MAX_SAFE_INTEGER : priorityOf(existing.source);
    if (
      existing === undefined ||
      candidatePriority < existingPriority ||
      (candidatePriority === existingPriority && candidate.order < existing.order)
    ) {
      candidatesBySlide.set(candidate.card.slide_id, candidate);
    }
  }
  const sorted = [...candidatesBySlide.values()].sort((left, right) => {
    const priority = priorityOf(left.source) - priorityOf(right.source);
    return (
      priority || left.order - right.order || left.card.slide_id.localeCompare(right.card.slide_id)
    );
  });
  const selectedSlideIds = new Set<string>();
  if (sorted.length <= limit) {
    for (const candidate of sorted) selectedSlideIds.add(candidate.card.slide_id);
  } else {
    for (const source of PERSONAL_FLOW_SOURCE_ORDER) {
      const candidate = sorted.find((item) => item.source === source);
      if (candidate !== undefined && selectedSlideIds.size < limit) {
        selectedSlideIds.add(candidate.card.slide_id);
      }
    }
    for (const candidate of sorted) {
      if (selectedSlideIds.size >= limit) break;
      selectedSlideIds.add(candidate.card.slide_id);
    }
  }
  const selected = sorted.filter((candidate) => selectedSlideIds.has(candidate.card.slide_id));
  return sections.map(({ key }) => ({
    key,
    cards: selected.filter((candidate) => candidate.key === key).map(({ card }) => card),
  }));
}

export function selectNextPersonalFlowSectionExpansion(input: {
  sections: PersonalFlowSectionExpansionCandidate[];
  budgets: PersonalFlowSectionBudgets;
  limit?: number;
}): PersonalFlowSectionKey | null {
  const limit = input.limit ?? PERSONAL_FLOW_PAGE_LIMIT;
  if (input.sections.some((section) => !section.isReady)) return null;
  const selected = selectPersonalFlowSectionCards(input.sections, limit);
  const selectedCount = selected.reduce((count, section) => count + section.cards.length, 0);
  const lowerSections = input.sections.slice(1);

  for (const section of lowerSections) {
    const budget = input.budgets[section.key];
    const mayHaveMore = budget < limit && section.cards.length >= budget;
    if (!mayHaveMore) continue;

    const selectedSection = selected.find((candidate) => candidate.key === section.key);
    const sentinelWasDeduplicated =
      section.cards.length > 0 && (selectedSection?.cards.length ?? 0) === 0;
    if (selectedCount < limit || sentinelWasDeduplicated) return section.key;
  }

  return null;
}

export function expandPersonalFlowSectionBudget(
  budgets: PersonalFlowSectionBudgets,
  section: PersonalFlowSectionKey
): PersonalFlowSectionBudgets {
  if (budgets[section] >= PERSONAL_FLOW_PAGE_LIMIT) return budgets;
  return { ...budgets, [section]: PERSONAL_FLOW_PAGE_LIMIT };
}
